import 'src/styles/post-processor-queue.css';

import { faCube, faMapMarkerAlt, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import MaintenanceHealthBadge from 'src/components/maintenance/MaintenanceHealthBadge';
import RunQueue from 'src/components/RunQueue';
import FeaturesContext from 'src/context/FeaturesContext';
import { api } from 'src/utils/api';
import { API_RESOURCES } from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import routes from 'src/utils/routes';
import { getRouteURI, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

const PostProcessorQueuePage = ({ user }) => {
  const { uuid } = useParams();
  const { addToast } = useToasts();
  const { features } = useContext(FeaturesContext);
  const isAiScadaIntegration = isFeatureEnabled(features, FEATURES.AI_SCADA_INTEGRATION);
  const isMaintenanceEnabled = isFeatureEnabled(features, FEATURES.EQUIPMENT_MAINTENANCE);

  const [postProcessor, setPostProcessor] = useState(null);
  const [subLocation, setSubLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOrderedLoadCarries, setIsOrderedLoadCarries] = useState(false);

  const fetchPostProcessor = useCallback(async () => {
    try {
      const data = await api.get(`${API_RESOURCES.POST_PROCESSOR}/${uuid}/`).json();
      const subLocationResponse = data.sub_location ? await api.get(
        `${API_RESOURCES.SUB_LOCATION}/${getUuid(data.sub_location)}/`
      ).json() : null;
      setPostProcessor(data);
      setSubLocation(subLocationResponse);
    } catch (error) {
      addToast('Failed to fetch post processor information', { appearance: 'error' });
    } finally {
      setLoading(false);
    }
  }, [uuid, addToast]);

  useEffect(() => {
    fetchPostProcessor();
  }, [fetchPostProcessor]);

  const handleOrderLoadCarriers = () => {
    addToast('Your order has been processed', { appearance: 'success' });
    setIsOrderedLoadCarries(true);
  };

  if (loading || !postProcessor) {
    return (
      <>
        <Header excludeFromHistory title='Loading' user={user} />
        <main role='main' className='text-center'>
          <Loader />
        </main>
      </>
    );
  }

  return (
    <>
      <Header title='Post processor information' user={user} />
      <div role='main' className='ppq-page'>
        <div className='ppq-container'>
          <p className='ppq-title'>
            <span>
              Post processor name:{' '}
              <span className='ppq-title-name'>{postProcessor?.name}</span>
            </span>
            {isMaintenanceEnabled && (
              <MaintenanceHealthBadge
                equipmentUri={postProcessor.uri}
                className='align-self-center'
                containerClassName='align-items-center'
                maintenanceRoute={routes.postProcessorMaintenance}
              />
            )}
          </p>

          <div className='ppq-location-card'>
            <div className='ppq-location-info'>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <div>
                <div className='ppq-location-name'>{postProcessor?.location_name}</div>
                <div className='ppq-location-room'>{subLocation?.name}</div>
              </div>
            </div>
            <div className='ppq-availability'>
              <span className='ppq-availability-dot' />
              <span>Available</span>
            </div>
          </div>

          {isMaintenanceEnabled && (
            <Link
              to={getRouteURI(routes.postProcessorMaintenance, { uuid })}
              className='link-btn'
            >
              <button type='button' className='btn btn-lg btn-primary btn-block fs-mobile-18 mb-3'>
                <FontAwesomeIcon icon={faScrewdriverWrench} className='mr-2' />
                Maintenance
              </button>
            </Link>
          )}

          <RunQueue workstation={postProcessor} filterKey='post_processor' />

          {isAiScadaIntegration && (
            <div className='ppq-load-carriers-card'>
              <div className='ppq-load-carriers-info'>
                <div className='ppq-load-carriers-title'>Load carriers</div>
                <div className='ppq-load-carriers-subtitle'>
                  Request additional load carriers for this station
                </div>
              </div>
              <Button variant='primary' size='sm' disabled={isOrderedLoadCarries} onClick={handleOrderLoadCarriers}>
                <FontAwesomeIcon icon={faCube} className='mr-1' />
                Order more load carriers
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

PostProcessorQueuePage.propTypes = {
  user: userPropType,
};

PostProcessorQueuePage.defaultProps = {
  user: null,
};

export default PostProcessorQueuePage;
